import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Button, Spinner, Alert } from 'react-bootstrap';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { config } from '../services/api';
import './EventDetail.css';
import { authService } from '../services/api';

// Add this at the top of the file, outside the component
const formatPrice = (price) => {
  if (!price) return 'Gratuit';
  // Remove decimal places and add thousands separator
  return parseInt(price).toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ") + ' DZD';
};

function EventDetail() {
  const [isAuthenticated, setIsAuthenticated] = useState(authService.isAuthenticated());
  const { id } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [countdown, setCountdown] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });

  // Add the missing handleReservationClick function
  const handleReservationClick = () => {
    navigate(`/reservation/${event.id}`);
  };

  useEffect(() => {
    const fetchEventDetail = async () => {
      try {
        const response = await axios.get(`${config.API_URL}/api/events/${id}/`);
        setEvent(response.data);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching event details:', error);
        setError('Impossible de charger les détails de l\'événement. Veuillez réessayer plus tard.');
        setLoading(false);
      }
    };

    fetchEventDetail();
  }, [id]);

  // Countdown timer effect
  useEffect(() => {
    if (!event || !event.date) return;

    const calculateTimeLeft = () => {
      const eventDate = new Date(`${event.date}T${event.time}`);
      const now = new Date();
      const difference = eventDate - now;

      if (difference <= 0) {
        return {
          days: 0,
          hours: 0,
          minutes: 0,
          seconds: 0
        };
      }

      return {
        days: Math.floor(difference / (1000 * 60 * 60 * 24)),
        hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((difference / 1000 / 60) % 60),
        seconds: Math.floor((difference / 1000) % 60)
      };
    };

    setCountdown(calculateTimeLeft());
    const timer = setInterval(() => {
      setCountdown(calculateTimeLeft());
    }, 1000);

    return () => clearInterval(timer);
  }, [event]);

  if (loading) {
    return (
      <Container className="py-5">
        <div className="text-center my-5">
          <Spinner animation="border" variant="danger" />
          <p className="mt-3">Chargement des détails de l'événement...</p>
        </div>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="py-5">
        <Alert variant="danger">
          <i className="fas fa-exclamation-circle me-2"></i>
          {error}
        </Alert>
        <div className="text-center mt-4">
          <Link to="/Evenements" className="btn btn-outline-danger">
            <i className="fas fa-arrow-left me-2"></i>Retour aux événements
          </Link>
        </div>
      </Container>
    );
  }

  if (!event) {
    return (
      <Container className="py-5">
        <Alert variant="warning">
          <i className="fas fa-exclamation-triangle me-2"></i>
          Événement non trouvé
        </Alert>
        <div className="text-center mt-4">
          <Link to="/Evenements" className="btn btn-outline-danger">
            <i className="fas fa-arrow-left me-2"></i>Retour aux événements
          </Link>
        </div>
      </Container>
    );
  }

  return (
    <div className="event-detail-page">
      {/* Event Header with Image */}
      <div className="event-header" style={{ 
        backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), url(${event.image})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        color: 'white',
        padding: '100px 0 50px',
        marginBottom: '30px'
      }}>
        <Container>
          <h1 className="display-4 fw-bold">{event.title}</h1>
          <div className="event-meta mt-4">
            <div className="d-flex flex-wrap align-items-center">
              <div className="me-4 mb-3">
                <i className="far fa-calendar-alt me-2"></i>
                {event.formatted_date}
              </div>
              <div className="me-4 mb-3">
                <i className="far fa-clock me-2"></i>
                {event.time}
              </div>
              <div className="me-4 mb-3">
                <i className="fas fa-map-marker-alt me-2"></i>
                {event.wilaya} - {event.wilaya_name}
              </div>

            </div>
          </div>
        </Container>
      </div>

      {/* Event Content */}
      <Container className="py-4">
        <Row>
          <Col lg={8}>

           {/* Price Section */}
           <section className="price-section" >
              <div className="container">
                <div className="price-info-card">
                  <div className="price-info-section">
                    <div className="price-icon">
                      <i className="fas fa-tag"></i>
                    </div>
                    <div className="price-details">
                      <h2 className="price-tag">{event.price ? `${event.price} DZD` : 'Gratuit'}</h2>
                      <p className="price-label">Prix par personne</p>
                    </div>
                  </div>
                  <div className="reserve-section">
                    <Button 
                      id="reserveButton" 
                      className="btn-reserve"
                      as={Link}
                      to={`/reservation/${event.id}`}
                      disabled={event.is_past || event.is_sold_out || event.registration_closed}
                    >
                      {event.is_past ? (
                        <>
                          <i className="fas fa-calendar-times"></i> Événement passé
                        </>
                      ) : event.is_sold_out ? (
                        <>
                          <i className="fas fa-ticket-alt"></i> Complet
                        </>
                      ) : event.registration_closed ? (
                        <>
                          <i className="fas fa-lock"></i> Inscriptions fermées
                        </>
                      ) : (
                        <>
                          <i className="fas fa-ticket-alt"></i> Réserver maintenant
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            </section>

            {/* Countdown Timer */}
            {countdown.days + countdown.hours + countdown.minutes + countdown.seconds > 0 && (
              <div className="event-countdown bg-white p-4 rounded shadow-sm mb-4">
                <h4 className="text-center mb-3">Compte à rebours</h4>
                <div className="d-flex justify-content-center">
                  <div className="countdown-item text-center mx-3">
                    <div className="countdown-value">{countdown.days}</div>
                    <div className="countdown-label">Jours</div>
                  </div>
                  <div className="countdown-item text-center mx-3">
                    <div className="countdown-value">{countdown.hours}</div>
                    <div className="countdown-label">Heures</div>
                  </div>
                  <div className="countdown-item text-center mx-3">
                    <div className="countdown-value">{countdown.minutes}</div>
                    <div className="countdown-label">Minutes</div>
                  </div>
                  <div className="countdown-item text-center mx-3">
                    <div className="countdown-value">{countdown.seconds}</div>
                    <div className="countdown-label">Secondes</div>
                  </div>
                </div>
              </div>
            )}
            
           
            
            <div className="event-description bg-white p-4 rounded shadow-sm mb-4">
              <h3 className="border-bottom pb-3 mb-4">À propos de cet événement</h3>
              <div dangerouslySetInnerHTML={{ __html: event.description }} />
            </div>

            {event.partner && (
              <div className="event-organizer bg-white p-4 rounded shadow-sm">
                <h3 className="border-bottom pb-3 mb-4">Organisé par</h3>
                <div className="d-flex align-items-center">
                  {event.partner.logo ? (
                    <img 
                      src={event.partner.logo} 
                      alt={event.partner.name} 
                      className="me-3" 
                      style={{ width: '80px', height: '80px', objectFit: 'contain' }}
                    />
                  ) : (
                    <div className="partner-placeholder me-3">
                      <i className="fas fa-building fa-3x text-muted"></i>
                    </div>
                  )}
                  <div>
                    <h4 className="mb-1">{event.partner.name}</h4>
                    {event.partner.description && (
                      <p className="text-muted mb-2">{event.partner.description}</p>
                    )}
                    {event.partner.website && (
                      <a href={event.partner.website} target="_blank" rel="noopener noreferrer" className="text-danger">
                        <i className="fas fa-globe me-1"></i> Site web
                      </a>
                    )}
                  </div>
                </div>
              </div>
            )}
          </Col>

          <Col lg={4}>
            <div className="event-sidebar bg-white p-4 rounded shadow-sm">
              <h3 className="border-bottom pb-3 mb-4">Réserver votre place</h3>
              
              {event.is_sold_out ? (
                <Alert variant="warning">
                  <i className="fas fa-exclamation-circle me-2"></i>
                  Cet événement est complet
                </Alert>
              ) : event.registration_closed ? (
                <Alert variant="info">
                  <i className="fas fa-info-circle me-2"></i>
                  Les inscriptions sont fermées
                </Alert>
              ) : (
                <>
                  <p>Places disponibles: {event.available_seats || 'Illimité'}</p>
                  {/* Reservation button */}
                  <div className="mt-4">
                    {isAuthenticated ? (
                      <Button 
                        variant="danger" 
                        size="lg" 
                        className="w-100"
                        onClick={handleReservationClick}
                        disabled={event.is_past || event.is_sold_out || event.registration_closed}
                      >
                        {event.is_past ? (
                          <>
                            <i className="fas fa-calendar-times me-2"></i>
                            Événement passé
                          </>
                        ) : event.is_sold_out ? (
                          <>
                            <i className="fas fa-ticket-alt me-2"></i>
                            Complet
                          </>
                        ) : event.registration_closed ? (
                          <>
                            <i className="fas fa-lock me-2"></i>
                            Inscriptions fermées
                          </>
                        ) : (
                          <>
                            <i className="fas fa-ticket-alt me-2"></i>
                            Réserver
                          </>
                        )}
                      </Button>
                    ) : (
                      <div>
                        <p className="text-center mb-2">Connectez-vous pour réserver</p>
                        <Link to="/login" className="btn btn-outline-danger w-100">
                          <i className="fas fa-sign-in-alt me-2"></i>
                          Se connecter
                        </Link>
                      </div>
                    )}
                  </div>
                  <p className="small text-muted">
                    <i className="fas fa-info-circle me-1"></i>
                    La réservation est obligatoire pour participer à cet événement.
                  </p>
                </>
              )}
              
              <hr className="my-4" />
              
              <div className="share-event">
                <h5 className="mb-3">Partager cet événement</h5>
                <div className="d-flex">
                  <a href={`https://www.facebook.com/sharer/sharer.php?u=${window.location.href}`} 
                     target="_blank" 
                     rel="noopener noreferrer" 
                     className="btn btn-outline-primary me-2">
                    <i className="fab fa-facebook-f"></i>
                  </a>
                  <a href={`https://twitter.com/intent/tweet?url=${window.location.href}&text=${event.title}`} 
                     target="_blank" 
                     rel="noopener noreferrer" 
                     className="btn btn-outline-info me-2">
                    <i className="fab fa-twitter"></i>
                  </a>
                  <a href={`https://wa.me/?text=${event.title} ${window.location.href}`} 
                     target="_blank" 
                     rel="noopener noreferrer" 
                     className="btn btn-outline-success">
                    <i className="fab fa-whatsapp"></i>
                  </a>
                </div>
              </div>
            </div>
          </Col>
        </Row>

        <div className="text-center mt-5">
          <Link to="/Evenements" className="btn btn-outline-danger">
            <i className="fas fa-arrow-left me-2"></i>Retour aux événements
          </Link>
        </div>
      </Container>
    </div>
  );
}

export default EventDetail;

